/**
 * ThemePurchaseCTA – Standardized purchase component for theme transactions.
 *
 * Uses Stripe's Payment Element (inline, no redirect) for one-time theme
 * payments.  Supports cards, Apple Pay, Google Pay, Link, and any other
 * payment method enabled on the Stripe account.
 *
 * Props:
 *  - projectId      {string|number} – ID of the theme_generation record to purchase.
 *  - projectMode    {string}        – 'quick' ($39) or 'custom' ($79).
 *  - paymentStatus  {string}        – Current payment status ('unpaid'|'processing'|'pending'|'paid').
 *  - projectStatus  {string}        – Current generation status (must be 'completed' to enable).
 *  - context        {string}        – 'generation' | 'projects' (for analytics/logging).
 *  - onPaymentSuccess {function}    – Callback invoked after payment succeeds (download triggered).
 *  - disabled       {boolean}       – Externally disable the button (e.g. still generating).
 */

import React, { useState } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import StripePaymentElement from './StripePaymentElement';

const ThemePurchaseCTA = ({
  projectId,
  projectMode = 'custom',
  paymentStatus = 'unpaid',
  projectStatus,
  context = 'projects',
  onPaymentSuccess,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const price = projectMode === 'quick' ? 49 : 149;

  // Step 1: ask the backend for a PaymentIntent client_secret
  const handleOpenPayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `/api/billing/pay-theme/${projectId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.charged === false) {
        // Creator / subscription bypass – no Stripe payment needed
        if (onPaymentSuccess) {
          onPaymentSuccess();
        } else {
          window.location.reload();
        }
      } else {
        // Show inline Payment Element
        setClientSecret(response.data.clientSecret);
        setPaymentAmount(response.data.amount);
      }
    } catch (err) {
      console.error('ThemePurchaseCTA payment error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: payment succeeded – trigger download automatically
  const handlePaymentSuccess = async (paymentIntent) => {
    setClientSecret(null);

    try {
      setDownloading(true);

      const response = await axios.get(`/api/download/${projectId}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `theme-${projectId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
    } catch (err) {
      console.error('Auto-download after payment failed:', err);
      setError('Payment successful! Please use the Download button to get your theme.');
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
    } finally {
      setDownloading(false);
    }
  };

  // Theme is already purchased – nothing to show
  if (paymentStatus === 'paid') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <CheckCircleIcon color="success" fontSize="small" />
        <Typography variant="body2" color="success.main">
          Theme purchased
        </Typography>
      </Box>
    );
  }

  // Generation not complete yet – button should be disabled
  const isReady = projectStatus === 'completed';

  // Payment Element is open
  if (clientSecret) {
    return (
      <Box sx={{ mt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <StripePaymentElement
          clientSecret={clientSecret}
          projectId={projectId}
          amount={paymentAmount || price * 100}
          onSuccess={handlePaymentSuccess}
          onCancel={() => setClientSecret(null)}
          returnUrl={`${window.location.href.split('?')[0]}?payment=success`}
        />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        startIcon={
          loading || downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <PaymentIcon />
          )
        }
        onClick={handleOpenPayment}
        disabled={loading || downloading || disabled || !isReady}
        fullWidth
        data-context={context}
      >
        {downloading ? 'Downloading…' : loading ? 'Loading…' : `Pay $${price} & Download`}
      </Button>

      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        display="block"
        sx={{ mt: 0.5 }}
      >
        Secure payment powered by Stripe
      </Typography>
    </Box>
  );
};

export default ThemePurchaseCTA;

